// ═══════════════════════════════════════════════════════════════════
// P0-6 · THE single pricing-presentation owner (website + edge)
// ═══════════════════════════════════════════════════════════════════

// This module is the ONLY place that knows:
// • which currencies exist and how they render
// • which display currency a pickup country implies
// • how an amount is converted between currencies (via MAD)

// Consumers:
// • website  → `src/lib/money.ts` re-exports this module (no re-impl)
// • edge     → Malak imports it directly (Deno)

// It is deliberately dependency-free and runtime-agnostic: no Deno
// APIs, no DOM, no localStorage. Display-currency *persistence* is a
// browser concern and stays in `src/lib/money.ts`.

// Adding a currency = one entry in CURRENCY_REGISTRY (+ an FX row in
// public.currency_rates). Nothing else changes.
// ═══════════════════════════════════════════════════════════════════

export interface CurrencyDef {
 code: string;
 name: string;
 symbol: string;
 position: 'before' | 'after';
 decimals: number;
}

export const CURRENCY_REGISTRY = {
 MAD: { code: 'MAD', name: 'Moroccan Dirham', symbol: 'DH', position: 'after', decimals: 0 },
 EUR: { code: 'EUR', name: 'Euro', symbol: '€', position: 'before', decimals: 0 },
 GBP: { code: 'GBP', name: 'British Pound', symbol: '£', position: 'before', decimals: 0 },
 SEK: { code: 'SEK', name: 'Swedish Krona', symbol: 'kr', position: 'before', decimals: 0 },
 DKK: { code: 'DKK', name: 'Danish Krone', symbol: 'kr', position: 'before', decimals: 0 },
 NOK: { code: 'NOK', name: 'Norwegian Krone', symbol: 'kr', position: 'before', decimals: 0 },
 USD: { code: 'USD', name: 'US Dollar', symbol: '$', position: 'before', decimals: 0 },
 CHF: { code: 'CHF', name: 'Swiss Franc', symbol: 'CHF', position: 'before', decimals: 0 },

 TND: { code: 'TND', name: 'Tunisian Dinar', symbol: 'DT', position: 'after', decimals: 3 },
 DZD: { code: 'DZD', name: 'Algerian Dinar', symbol: 'DA', position: 'after', decimals: 2 },

 ALL: { code: 'ALL', name: 'Albanian Lek', symbol: 'L', position: 'after', decimals: 2 },
 BYN: { code: 'BYN', name: 'Belarusian Ruble', symbol: 'Br', position: 'after', decimals: 2 },
 BAM: { code: 'BAM', name: 'Bosnia-Herzegovina Convertible Mark', symbol: 'KM', position: 'after', decimals: 2 },
 BGN: { code: 'BGN', name: 'Bulgarian Lev', symbol: 'лв', position: 'after', decimals: 2 },
 CZK: { code: 'CZK', name: 'Czech Koruna', symbol: 'Kč', position: 'after', decimals: 2 },
 HUF: { code: 'HUF', name: 'Hungarian Forint', symbol: 'Ft', position: 'after', decimals: 2 },
 ISK: { code: 'ISK', name: 'Icelandic Króna', symbol: 'kr', position: 'after', decimals: 0 },
 MDL: { code: 'MDL', name: 'Moldovan Leu', symbol: 'L', position: 'after', decimals: 2 },
 MKD: { code: 'MKD', name: 'Macedonian Denar', symbol: 'ден', position: 'after', decimals: 2 },
 PLN: { code: 'PLN', name: 'Polish Złoty', symbol: 'zł', position: 'after', decimals: 2 },
 RON: { code: 'RON', name: 'Romanian Leu', symbol: 'lei', position: 'after', decimals: 2 },
 RSD: { code: 'RSD', name: 'Serbian Dinar', symbol: 'din', position: 'after', decimals: 2 },
 TRY: { code: 'TRY', name: 'Turkish Lira', symbol: '₺', position: 'before', decimals: 2 },
 UAH: { code: 'UAH', name: 'Ukrainian Hryvnia', symbol: '₴', position: 'before', decimals: 2 },

 AOA: { code: 'AOA', name: 'Angolan Kwanza', symbol: 'Kz', position: 'after', decimals: 2 },
 XOF: { code: 'XOF', name: 'West African CFA Franc', symbol: 'CFA', position: 'after', decimals: 0 },
 XAF: { code: 'XAF', name: 'Central African CFA Franc', symbol: 'FCFA', position: 'after', decimals: 0 },
 BWP: { code: 'BWP', name: 'Botswana Pula', symbol: 'P', position: 'before', decimals: 2 },
 BIF: { code: 'BIF', name: 'Burundian Franc', symbol: 'FBu', position: 'after', decimals: 0 },
 CVE: { code: 'CVE', name: 'Cape Verdean Escudo', symbol: '$', position: 'after', decimals: 2 },
 KMF: { code: 'KMF', name: 'Comorian Franc', symbol: 'CF', position: 'after', decimals: 0 },
 CDF: { code: 'CDF', name: 'Congolese Franc', symbol: 'FC', position: 'after', decimals: 2 },
 DJF: { code: 'DJF', name: 'Djiboutian Franc', symbol: 'Fdj', position: 'after', decimals: 0 },
 EGP: { code: 'EGP', name: 'Egyptian Pound', symbol: 'E£', position: 'before', decimals: 2 },
 ERN: { code: 'ERN', name: 'Eritrean Nakfa', symbol: 'Nfk', position: 'before', decimals: 2 },
 SZL: { code: 'SZL', name: 'Swazi Lilangeni', symbol: 'E', position: 'before', decimals: 2 },
 ETB: { code: 'ETB', name: 'Ethiopian Birr', symbol: 'Br', position: 'before', decimals: 2 },
 GMD: { code: 'GMD', name: 'Gambian Dalasi', symbol: 'D', position: 'before', decimals: 2 },
 GHS: { code: 'GHS', name: 'Ghanaian Cedi', symbol: '₵', position: 'before', decimals: 2 },
 GNF: { code: 'GNF', name: 'Guinean Franc', symbol: 'FG', position: 'after', decimals: 0 },
 KES: { code: 'KES', name: 'Kenyan Shilling', symbol: 'KSh', position: 'before', decimals: 2 },
 LSL: { code: 'LSL', name: 'Lesotho Loti', symbol: 'L', position: 'before', decimals: 2 },
 LRD: { code: 'LRD', name: 'Liberian Dollar', symbol: '$', position: 'before', decimals: 2 },
 LYD: { code: 'LYD', name: 'Libyan Dinar', symbol: 'LD', position: 'after', decimals: 3 },
 MGA: { code: 'MGA', name: 'Malagasy Ariary', symbol: 'Ar', position: 'after', decimals: 0 },
 MWK: { code: 'MWK', name: 'Malawian Kwacha', symbol: 'MK', position: 'before', decimals: 2 },
 MRU: { code: 'MRU', name: 'Mauritanian Ouguiya', symbol: 'UM', position: 'after', decimals: 2 },
 MUR: { code: 'MUR', name: 'Mauritian Rupee', symbol: '₨', position: 'before', decimals: 2 },
 MZN: { code: 'MZN', name: 'Mozambican Metical', symbol: 'MT', position: 'after', decimals: 2 },
 NAD: { code: 'NAD', name: 'Namibian Dollar', symbol: '$', position: 'before', decimals: 2 },
 NGN: { code: 'NGN', name: 'Nigerian Naira', symbol: '₦', position: 'before', decimals: 2 },
 RWF: { code: 'RWF', name: 'Rwandan Franc', symbol: 'FRw', position: 'after', decimals: 0 },
 STN: { code: 'STN', name: 'São Tomé Dobra', symbol: 'Db', position: 'after', decimals: 2 },
 SCR: { code: 'SCR', name: 'Seychellois Rupee', symbol: '₨', position: 'before', decimals: 2 },
 SLE: { code: 'SLE', name: 'Sierra Leonean Leone', symbol: 'Le', position: 'before', decimals: 2 },
 SOS: { code: 'SOS', name: 'Somali Shilling', symbol: 'Sh', position: 'before', decimals: 2 },
 ZAR: { code: 'ZAR', name: 'South African Rand', symbol: 'R', position: 'before', decimals: 2 },
 SSP: { code: 'SSP', name: 'South Sudanese Pound', symbol: '£', position: 'before', decimals: 2 },
 SDG: { code: 'SDG', name: 'Sudanese Pound', symbol: 'SDG', position: 'before', decimals: 2 },
 TZS: { code: 'TZS', name: 'Tanzanian Shilling', symbol: 'TSh', position: 'before', decimals: 2 },
 UGX: { code: 'UGX', name: 'Ugandan Shilling', symbol: 'USh', position: 'before', decimals: 0 },
 ZMW: { code: 'ZMW', name: 'Zambian Kwacha', symbol: 'ZK', position: 'before', decimals: 2 },
 ZWG: { code: 'ZWG', name: 'Zimbabwe Gold', symbol: 'ZiG', position: 'before', decimals: 2 },
} as const satisfies Record<string, CurrencyDef>;

export type SupportedCurrency = keyof typeof CURRENCY_REGISTRY;

export const DEFAULT_AVAILABLE_CURRENCIES: SupportedCurrency[] =
 ['MAD', 'EUR', 'GBP', 'SEK', 'DKK', 'NOK', 'CHF', 'USD'];

export const RATE_PIVOT_CURRENCY: SupportedCurrency = 'MAD';

export const BASE_CURRENCY: SupportedCurrency = RATE_PIVOT_CURRENCY;

export const REPORTING_CURRENCY: SupportedCurrency = 'EUR';

export const PRICING_CURRENCIES: SupportedCurrency[] = ['MAD', 'EUR', 'GBP', 'SEK', 'DKK', 'NOK'];

export interface FxRates { [currency: string]: number }

export const FALLBACK_RATES: FxRates = {
 MAD: 1,
 EUR: 0.093,
 GBP: 0.080,
 USD: 0.100,
 CHF: 0.090,
 NOK: 0.99,
 SEK: 1.06,
 DKK: 0.69,
};

export function isSupportedCurrency(code: string | null | undefined): code is SupportedCurrency {
 return !!code && code.toUpperCase() in CURRENCY_REGISTRY;
}

export function normalizeCurrency(code: string | null | undefined): SupportedCurrency {
 if (!code) return BASE_CURRENCY;
 const upper = code.toUpperCase();
 return isSupportedCurrency(upper) ? (upper as SupportedCurrency) : BASE_CURRENCY;
}

export function getCurrencyDef(code: string): CurrencyDef {
 return (CURRENCY_REGISTRY as Record<string, CurrencyDef>)[normalizeCurrency(code)];
}

export function getCurrencySymbol(code: string): string {
 return isSupportedCurrency(code) ? getCurrencyDef(code).symbol : code;
}

export function getCurrencyName(code: string): string {
 return isSupportedCurrency(code) ? getCurrencyDef(code).name : code;
}

export interface CurrencyOption { code: SupportedCurrency; name: string; symbol: string }

function toOptions(codes: readonly string[]): CurrencyOption[] {
 const seen = new Set<string>();
 const out: CurrencyOption[] = [];
 for (const raw of codes) {
 const code = String(raw).toUpperCase();
 if (!isSupportedCurrency(code) || seen.has(code)) continue;
 seen.add(code);
 const def = CURRENCY_REGISTRY[code as SupportedCurrency];
 out.push({ code: code as SupportedCurrency, name: def.name, symbol: def.symbol });
 }
 return out;
}

export function getSupportedCurrencies(available?: readonly string[] | null): CurrencyOption[] {
 return toOptions(available && available.length ? available : DEFAULT_AVAILABLE_CURRENCIES);
}

export function getPricingCurrencies(available?: readonly string[] | null): CurrencyOption[] {
 return toOptions(available && available.length ? available : PRICING_CURRENCIES);
}

const COUNTRY_CURRENCY: Record<string, SupportedCurrency> = {
 MA: 'MAD',
 TN: 'TND',
 DZ: 'DZD',
 GB: 'GBP', UK: 'GBP',
 SE: 'SEK',
 DK: 'DKK',
 NO: 'NOK',
 CH: 'CHF',
 US: 'USD',
 FR: 'EUR', ES: 'EUR', IT: 'EUR', DE: 'EUR', BE: 'EUR', NL: 'EUR', PT: 'EUR',
 IE: 'EUR', AT: 'EUR', FI: 'EUR', GR: 'EUR', LU: 'EUR', SI: 'EUR', SK: 'EUR',
 PL: 'EUR', CZ: 'EUR', RO: 'EUR', BG: 'EUR', HU: 'EUR',
};

const COUNTRY_NAME_ISO: Record<string, string> = {
 morocco: 'MA', maroc: 'MA',
 tunisia: 'TN', tunisie: 'TN', algeria: 'DZ', algerie: 'DZ', 'algérie': 'DZ',
 'united kingdom': 'GB', uk: 'GB', 'great britain': 'GB', england: 'GB',
 scotland: 'GB', wales: 'GB',
 sweden: 'SE', denmark: 'DK', norway: 'NO', switzerland: 'CH',
 'united states': 'US',
 france: 'FR', spain: 'ES', italy: 'IT', germany: 'DE', belgium: 'BE',
 netherlands: 'NL', portugal: 'PT', ireland: 'IE', austria: 'AT',
 finland: 'FI', greece: 'GR', luxembourg: 'LU', slovenia: 'SI',
 slovakia: 'SK', poland: 'PL', czechia: 'CZ', 'czech republic': 'CZ',
 romania: 'RO', bulgaria: 'BG', hungary: 'HU',
};

export function currencyForCountry(country: string | null | undefined): SupportedCurrency | null {
 if (!country) return null;
 const raw = country.trim();
 if (!raw) return null;
 const direct = COUNTRY_CURRENCY[raw.toUpperCase()];
 if (direct) return direct;
 const iso = COUNTRY_NAME_ISO[raw.toLowerCase()];
 return iso ? COUNTRY_CURRENCY[iso] ?? null : null;
}

export function resolveCurrency(code: string | null | undefined): SupportedCurrency | null {
 if (!code) return null;
 const upper = String(code).toUpperCase();
 return isSupportedCurrency(upper) ? (upper as SupportedCurrency) : null;
}

function rateOrNull(currency: string, fxRates?: FxRates): number | null {
 const code = resolveCurrency(currency);
 if (!code) return null;
 if (code === RATE_PIVOT_CURRENCY) return 1;
 const rate = fxRates?.[code] ?? FALLBACK_RATES[code];
 return Number.isFinite(rate) && (rate as number) > 0 ? (rate as number) : null;
}

export function canConvert(from: string, to: string, fxRates?: FxRates): boolean {
 return convertOrNull(1, from, to, fxRates) !== null;
}

export function convertOrNull(amount: number, from: string, to: string, fxRates?: FxRates): number | null {
 if (!Number.isFinite(amount)) return null;
 const src = from ? String(from).toUpperCase() : '';
 const dst = to ? String(to).toUpperCase() : '';
 if (src && src === dst) return amount;
 const rFrom = rateOrNull(src, fxRates);
 const rTo = rateOrNull(dst, fxRates);
 if (rFrom === null || rTo === null) return null;
 return (amount / rFrom) * rTo;
}

export function convertFromMAD(amountMad: number, displayCurrency: SupportedCurrency, fxRates?: FxRates): number {
 const converted = convertOrNull(amountMad, RATE_PIVOT_CURRENCY, displayCurrency, fxRates);
 return converted === null ? NaN : converted;
}

export function convertToMAD(amount: number, fromCurrency: SupportedCurrency | string, fxRates?: FxRates): number {
 const converted = convertOrNull(amount, fromCurrency, RATE_PIVOT_CURRENCY, fxRates);
 return converted === null ? NaN : converted;
}

export function convertCurrency(amount: number, from: string, to: string, fxRates?: FxRates): number {
 const converted = convertOrNull(amount, from, to, fxRates);
 return converted === null ? NaN : converted;
}

export function convert(amount: number, from: string, to: string, fxRates?: FxRates): number {
 return convertCurrency(amount, from, to, fxRates);
}

export const FX_STALE_AFTER_MS = 24 * 60 * 60 * 1000;

export function isRateFresh(updatedAt: string | Date | null | undefined, now: number = Date.now()): boolean {
 if (!updatedAt) return false;
 const ts = updatedAt instanceof Date ? updatedAt.getTime() : Date.parse(updatedAt);
 if (!Number.isFinite(ts)) return false;
 return now - ts <= FX_STALE_AFTER_MS;
}

export function eurPerUnit(currency: string, fxRates?: FxRates): number | null {
 const code = normalizeCurrency(currency);
 if (code === REPORTING_CURRENCY) return 1;
 const rates = fxRates;
 if (!rates) return null;
 const perPivot = rates[code];
 const eurPerPivot = rates[REPORTING_CURRENCY];
 if (!perPivot || !eurPerPivot || perPivot <= 0 || eurPerPivot <= 0) return null;

 return eurPerPivot / perPivot;
}

export function reportingAmountEur(
 amount: number,
 currency: string,
 snapshot: number | null | undefined,
): number | null {
 if (!Number.isFinite(amount)) return null;
 if (normalizeCurrency(currency) === REPORTING_CURRENCY) return amount;
 if (snapshot == null || !Number.isFinite(snapshot) || snapshot <= 0) return null;
 return amount * snapshot;
}

export const DISPLAY_MAX_DECIMALS = 2;

export function render(
 amount: number,
 currency: SupportedCurrency,
 opts?: { showSymbol?: boolean; decimals?: number },
): string {
 const def = getCurrencyDef(currency);
 const decimals = Math.min(opts?.decimals ?? def.decimals, DISPLAY_MAX_DECIMALS);
 const rounded = decimals === 0 ? Math.floor(amount) : Number(amount.toFixed(decimals));
 const formatted = rounded.toLocaleString('en-US', {
 minimumFractionDigits: decimals,
 maximumFractionDigits: decimals,
 });
 if (opts?.showSymbol === false) return formatted;
 return def.position === 'after' ? `${formatted} ${def.symbol}` : `${def.symbol}${formatted}`;
}

export function formatAmount(
 amount: number,
 currency: string,
 opts?: { showSymbol?: boolean; decimals?: number },
): string {
 return render(amount, normalizeCurrency(currency), opts);
}

export function formatAmountIn(
 amount: number,
 sourceCurrency: string,
 displayCurrency: SupportedCurrency,
 fxRates?: FxRates,
 opts?: { showSymbol?: boolean; decimals?: number },
): string {
 const converted = convertOrNull(amount, sourceCurrency, displayCurrency, fxRates);
 if (converted === null) {
 const native = resolveCurrency(sourceCurrency);
 return native ? render(amount, native, opts) : String(amount);
 }
 return render(converted, displayCurrency, opts);
}

export function formatMoney(
 amountMad: number,
 displayCurrency: SupportedCurrency = BASE_CURRENCY,
 fxRates?: FxRates,
 opts?: { showSymbol?: boolean; decimals?: number },
): string {
 return formatAmountIn(amountMad, RATE_PIVOT_CURRENCY, displayCurrency, fxRates, opts);
}

export function formatMAD(amountMad: number): string {
 return `${Math.floor(amountMad)} DH`;
}

export function formatMoneyForConversation(args: {
 amount: number | null | undefined;
 currency?: string | null;
 pickupCountry?: string | null;
 displayCurrency?: string | null;
 fxRates?: FxRates;
 withSecondary?: boolean;
}): string | null {
 const { amount } = args;
 if (amount == null || !Number.isFinite(Number(amount))) return null;
 const source = normalizeCurrency(args.currency);
 const display = args.displayCurrency && isSupportedCurrency(args.displayCurrency)
 ? normalizeCurrency(args.displayCurrency)
 : (currencyForCountry(args.pickupCountry) ?? source);

 const primary = formatAmountIn(Number(amount), source, display, args.fxRates);

 if (args.withSecondary === false || display === BASE_CURRENCY || source === display) return primary;
 if (!canConvert(source, BASE_CURRENCY, args.fxRates)) return primary;
 const secondary = formatAmountIn(Number(amount), source, BASE_CURRENCY, args.fxRates);
 return `${primary} (${secondary})`;
}
